import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerOrders } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'awaiting_confirmation', label: 'Awaiting Confirmation' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const BuyerOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const limit = 20;

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit, sort: sortField, order: sortDir };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await fetchBuyerOrders(params);
      setOrders(res.orders || res.items || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, sortField, sortDir, search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { setPage(1); }, [statusFilter, sortField, sortDir, search]);

  const filteredOrders = search
    ? orders.filter(o =>
        (o.order_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.items || []).some((i: any) => (i.title || '').toLowerCase().includes(search.toLowerCase()))
      )
    : orders;

  return (
    <BuyerPortalLayout title="Purchase Orders">
      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by order number, product..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:bg-white outline-none transition-all"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm">
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={`${sortField}-${sortDir}`} onChange={e => {
            const [f, d] = e.target.value.split('-') as [string, 'ASC' | 'DESC'];
            setSortField(f); setSortDir(d);
          }} className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm">
            <option value="created_at-DESC">Newest First</option>
            <option value="created_at-ASC">Oldest First</option>
            <option value="total_amount-DESC">Highest Value</option>
            <option value="total_amount-ASC">Lowest Value</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-5xl mb-4">📋</p>
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            {search || statusFilter ? 'No orders match your filters' : 'No orders yet'}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {search || statusFilter ? 'Try different search terms.' : 'Start shopping to see your orders here.'}
          </p>
          {!search && !statusFilter && (
            <Link to="/account/marketplace" className="inline-flex px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition">
              Browse Marketplace
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Order #</th>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Date</th>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Items</th>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Total</th>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredOrders.map((o: any) => (
                    <tr key={o.id} className="hover:bg-gray-50 transition group">
                      <td className="px-5 py-3 font-mono text-xs font-medium text-gray-900">{o.order_number}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{o.item_count || o.items?.length || 0}</td>
                      <td className="px-5 py-3 font-bold text-gray-900">{formatMoney(Number(o.total_amount) || 0, o || DEFAULT_CURRENCY)}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          o.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          o.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          o.status === 'delivered' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {(o.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <Link to={`/account/orders/${o.id}`} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl disabled:opacity-40 bg-white hover:bg-gray-50">
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-500">Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={orders.length < limit}
              className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl disabled:opacity-40 bg-white hover:bg-gray-50">
              Next
            </button>
          </div>
        </>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerOrdersPage;
